import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Plus, Home, ImageIcon, Video, Loader2, Check, Trash2 } from 'lucide-react'
import roomService, { Room, CreateRoomData } from '../services/roomService'

/** Draft capacity while typing custom values (empty string allowed; `|| 1` would block editing). */
type RoomFormState = Omit<CreateRoomData, 'capacity'> & { capacity: number | '' }
import mediaService from '../services/mediaService'
import toast from 'react-hot-toast'
import ConfirmationDialog from './ConfirmationDialog'
import { MEDIA_SECTIONS, validateImageFiles, validateVideoFile } from '../config/mediaValidation'
import { validateRoom } from '../validation'

const roomTypes = [
  { value: 'PRIVATE', label: 'Private', capacity: 1 },
  { value: 'DOUBLE', label: 'Shared - Double', capacity: 2 },
  { value: 'TRIPLE', label: 'Shared - Triple', capacity: 3 },
  { value: 'QUADRUPLE', label: 'Shared - Quadruple', capacity: 4 },
  { value: 'CUSTOM', label: 'Custom', capacity: null as number | null },
] as const

export interface RoomTypesManageModalProps {
  isOpen: boolean
  onClose: () => void
  schoolId: string
  schoolName?: string
  /** Called after rooms change so parent can refresh (e.g. course accommodation dropdown). */
  onRoomsUpdated: () => void
}

const RoomTypesManageModal: React.FC<RoomTypesManageModalProps> = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  onRoomsUpdated,
}) => {
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [formData, setFormData] = useState<RoomFormState>({
    type: 'PRIVATE',
    name: '',
    capacity: 1,
    notes: '',
    images: [],
    video: '',
    isActive: true,
  })
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [roomToDelete, setRoomToDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [roomMediaUrlMap, setRoomMediaUrlMap] = useState<Record<string, string>>({})

  const getRoomMediaDisplayUrl = useCallback(
    (value: string): string => {
      if (!value) return value
      const fileKey =
        value.startsWith('http://') || value.startsWith('https://')
          ? mediaService.extractFileKeyFromUrl(value)
          : value
      if (!fileKey) return value
      return roomMediaUrlMap[fileKey] || value
    },
    [roomMediaUrlMap]
  )

  const loadRooms = useCallback(async () => {
    if (!schoolId) return
    setLoading(true)
    try {
      const data = await roomService.getSchoolRooms(schoolId)
      setRooms(Array.isArray(data) ? data : [])
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.message || 'Failed to load room types')
      setRooms([])
    } finally {
      setLoading(false)
    }
  }, [schoolId])

  useEffect(() => {
    if (isOpen && schoolId) {
      loadRooms()
      setShowCreateForm(false)
    }
  }, [isOpen, schoolId, loadRooms])

  useEffect(() => {
    const collect = (value: string): string | null => {
      if (!value) return null
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return mediaService.extractFileKeyFromUrl(value)
      }
      return value
    }
    const fileKeys = new Set<string>()
    rooms.forEach((room) => {
      room.images?.forEach((url) => {
        const k = collect(url)
        if (k) fileKeys.add(k)
      })
      if (room.video) {
        const k = collect(room.video)
        if (k) fileKeys.add(k)
      }
    })
    if (showCreateForm) {
      formData.images?.forEach((url) => {
        const k = collect(url)
        if (k) fileKeys.add(k)
      })
      if (formData.video) {
        const k = collect(formData.video)
        if (k) fileKeys.add(k)
      }
    }
    const map: Record<string, string> = {}
    fileKeys.forEach((key) => {
      map[key] = mediaService.getMediaUrlFromKey(key)
    })
    setRoomMediaUrlMap(map)
  }, [rooms, showCreateForm, formData.images, formData.video])

  const resetForm = () => {
    setFormData({
      type: 'PRIVATE',
      name: '',
      capacity: 1,
      notes: '',
      images: [],
      video: '',
      isActive: true,
    })
  }

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!schoolId) return
    const capacityNum = formData.capacity === '' ? NaN : formData.capacity
    const roomErrors = validateRoom({ ...formData, capacity: capacityNum })
    if (Object.keys(roomErrors).length > 0) {
      toast.error(Object.values(roomErrors)[0] as string)
      return
    }
    try {
      await roomService.createRoom(schoolId, { ...formData, capacity: capacityNum })
      toast.success('Room type created')
      resetForm()
      setShowCreateForm(false)
      await loadRooms()
      onRoomsUpdated()
    } catch (err: any) {
      console.error(err)
      toast.error(err?.response?.data?.message || 'Failed to create room type')
    }
  }

  const handleToggleStatus = async (roomId: string) => {
    try {
      await roomService.toggleRoomStatus(roomId)
      toast.success('Status updated')
      await loadRooms()
      onRoomsUpdated()
    } catch {
      toast.error('Failed to update status')
    }
  }

  const handleDeleteConfirm = async () => {
    if (!roomToDelete) return
    try {
      setDeleting(true)
      await roomService.deleteRoom(roomToDelete)
      toast.success('Room type removed')
      setShowDeleteDialog(false)
      setRoomToDelete(null)
      await loadRooms()
      onRoomsUpdated()
    } catch {
      toast.error('Failed to delete room type')
    } finally {
      setDeleting(false)
    }
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length || !schoolId) return
    const fileList = Array.from(files)
    const currentCount = formData.images?.length ?? 0
    const validation = validateImageFiles(fileList, 'roomImages', currentCount)
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid image')
      e.target.value = ''
      return
    }
    setUploadingImage(true)
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]
        const result = await mediaService.uploadFile(file, 'generalImage', schoolId)
        if (result?.data?.fileKey) {
          setFormData((prev) => ({
            ...prev,
            images: [...(prev.images || []), result.data.fileKey],
          }))
        }
      }
      toast.success('Image(s) uploaded')
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed')
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !schoolId) return
    const validation = validateVideoFile(file, 'roomVideo', formData.video ? 1 : 0)
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid video')
      e.target.value = ''
      return
    }
    setUploadingVideo(true)
    try {
      const result = await mediaService.uploadFile(file, 'generalVideo', schoolId)
      if (result?.data?.fileKey) {
        setFormData((prev) => ({ ...prev, video: result.data.fileKey }))
        toast.success('Video uploaded')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed')
    } finally {
      setUploadingVideo(false)
      e.target.value = ''
    }
  }

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }))
  }

  const removeVideo = () => setFormData((prev) => ({ ...prev, video: '' }))

  if (!isOpen || typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="room-types-modal-title"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div>
              <h2 id="room-types-modal-title" className="text-lg font-semibold text-gray-900">
                Accommodation room types
              </h2>
              {schoolName ? (
                <p className="text-sm text-gray-600 mt-0.5">{schoolName}</p>
              ) : null}
              <p className="text-xs text-gray-500 mt-1">
                Add room types here without leaving your course. Your course draft stays on this page.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm((v) => !v)
                  if (showCreateForm) resetForm()
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {showCreateForm ? 'Cancel add' : 'Add room type'}
              </button>
            </div>

            {showCreateForm && (
              <form onSubmit={handleCreateSubmit} className="p-4 border border-primary-200 rounded-lg bg-primary-50/40 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">New room type</h3>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Room type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      const selectedType = e.target.value as Room['type']
                      const selectedRoomType = roomTypes.find((rt) => rt.value === selectedType)
                      setFormData({
                        ...formData,
                        type: selectedType,
                        capacity:
                          selectedRoomType?.capacity !== null && selectedRoomType?.capacity !== undefined
                            ? selectedRoomType.capacity
                            : formData.capacity === '' || typeof formData.capacity === 'number'
                              ? formData.capacity
                              : 1,
                      })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    {roomTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="e.g. Deluxe private room"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Capacity *</label>
                  <input
                    type="number"
                    value={
                      formData.type === 'CUSTOM' && formData.capacity === '' ? '' : formData.capacity
                    }
                    onChange={(e) => {
                      if (formData.type !== 'CUSTOM') return
                      const v = e.target.value
                      if (v === '') {
                        setFormData({ ...formData, capacity: '' })
                        return
                      }
                      const n = parseInt(v, 10)
                      if (!Number.isNaN(n)) setFormData({ ...formData, capacity: n })
                    }}
                    onWheel={(ev) => (ev.target as HTMLInputElement).blur()}
                    readOnly={formData.type !== 'CUSTOM'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${
                      formData.type === 'CUSTOM' ? '' : 'bg-gray-50 text-gray-600'
                    }`}
                    min={1}
                    max={20}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Optional details"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Images</label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept={MEDIA_SECTIONS.roomImages.acceptImages}
                    multiple
                    className="hidden"
                    onChange={handleImageSelect}
                  />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-600"
                  >
                    {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                    {uploadingImage ? 'Uploading…' : 'Upload images'}
                  </button>
                  {(formData.images?.length ?? 0) > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(formData.images || []).map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={getRoomMediaDisplayUrl(url)}
                            alt=""
                            className="h-14 w-14 object-cover rounded border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Video</label>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept={MEDIA_SECTIONS.roomVideo.acceptVideos}
                    className="hidden"
                    onChange={handleVideoSelect}
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-600"
                  >
                    {uploadingVideo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                    {uploadingVideo ? 'Uploading…' : 'Upload video'}
                  </button>
                  {formData.video ? (
                    <button type="button" onClick={removeVideo} className="ml-2 text-sm text-red-600">
                      Remove video
                    </button>
                  ) : null}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.isActive !== false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  Active (available for courses)
                </label>
                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                >
                  Create room type
                </button>
              </form>
            )}

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Your room types</h3>
              {loading ? (
                <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
              ) : rooms.length === 0 ? (
                <div className="text-center py-8 px-4 border border-dashed border-gray-200 rounded-lg bg-gray-50">
                  <Home className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No room types yet.</p>
                  <p className="text-xs text-gray-500 mt-1">Use &quot;Add room type&quot; above to create one.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {rooms.map((room) => (
                    <li
                      key={room._id}
                      className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                    >
                      {room.images?.[0] ? (
                        <img
                          src={getRoomMediaDisplayUrl(room.images[0])}
                          alt=""
                          className="h-14 w-14 object-cover rounded flex-shrink-0"
                        />
                      ) : (
                        <div className="h-14 w-14 rounded bg-gray-100 flex-shrink-0 flex items-center justify-center">
                          <Home className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{room.name}</p>
                        <p className="text-xs text-gray-500">
                          {room.type} · Capacity {room.capacity}
                          {room.isActive ? '' : ' · Inactive'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(room._id)}
                          className={`p-2 rounded-md ${
                            room.isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-gray-400 hover:bg-gray-50'
                          }`}
                          title={room.isActive ? 'Active' : 'Inactive'}
                        >
                          {room.isActive ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRoomToDelete(room._id)
                            setShowDeleteDialog(true)
                          }}
                          className="p-2 rounded-md text-red-600 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Done
            </button>
          </div>
        </motion.div>

        <ConfirmationDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false)
            setRoomToDelete(null)
          }}
          onConfirm={handleDeleteConfirm}
          title="Delete room type"
          message="Remove this room type? Courses using it may need to be updated."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="red"
          isLoading={deleting}
        />
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

export default RoomTypesManageModal
